import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type { Turnstile, TurnstileAssignment, AccountPerson, PaginatedResponse } from "../Component/Application/turnstile/types"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    Object.assign(config.headers, getExhibitionHeader());
    }
    return config
  },
  (error) => Promise.reject(error),
)

export const TurnstileService = {
  getAll: async (page = 1, pageSize = 10, search = ""): Promise<PaginatedResponse<Turnstile>> => {
    const response = await api.get("/api/core/turnstiles/", {
      params: { page, page_size: pageSize, search },
    })
    return response.data
  },

  getById: async (id: number): Promise<Turnstile> => {
    const response = await api.get(`/api/core/turnstiles/${id}/`)
    return response.data
  },

  create: async (data: Omit<Turnstile, "id">): Promise<Turnstile> => {
    const response = await api.post("/api/core/turnstiles/create/", data)
    return response.data
  },

  update: async (id: number, data: Omit<Turnstile, "id">): Promise<Turnstile> => {
    const response = await api.put(`/api/core/turnstiles/${id}/update/`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/core/turnstiles/${id}/delete/`)
  },
}

export const TurnstileAssignmentService = {
  getAll: async (
    page = 1,
    pageSize = 10,
    search = "",
    status = "",
  ): Promise<PaginatedResponse<TurnstileAssignment>> => {
    const response = await api.get("/api/core/assignments/", {
      params: { page, page_size: pageSize, search, status },
    })
    return response.data
  },

  create: async (data: {
    user: number | null
    turnstile: number
    status: "active" | "revoked" | "pending"
  }): Promise<TurnstileAssignment> => {
    const response = await api.post("/api/core/assignments/create/", data)
    return response.data
  },

  update: async (
    id: number,
    data: {
      user: number | null
      turnstile: number
      status: "active" | "revoked" | "pending"
    },
  ): Promise<TurnstileAssignment> => {
    const response = await api.put(`/api/core/assignments/${id}/update/`, data)
    return response.data
  },

  revoke: async (id: number): Promise<TurnstileAssignment> => {
    const response = await api.post(`/api/core/assignments/${id}/revoke/`)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/core/assignments/${id}/delete/`)
  },

  getAccountsForAssignment: async (): Promise<AccountPerson[]> => {
    const response = await api.get("/api/core/accounts-for-assignment/")
    return response.data
  },

  getAvailableTurnstiles: async (): Promise<Turnstile[]> => {
    const response = await api.get("/api/core/available-turnstiles/")
    return response.data
  },
}
